import React, { useEffect, useMemo, useRef } from 'react'
import { GetServerSideProps } from 'next'
import PageContainer from 'common/PageContainer'
import {
  seFriendlyPhoneNumber,
  specialties,
  HowItWorksPsychData,
  reviewPanelItems,
  providerTypes,
  SeFriendlyPageType,
  SeFriendlyHubPageTitle,
} from 'app/SeFriendly/constants'
import HeadComponent from 'common/HeadComponent'
import styles from '../index.module.scss'
import { useRouter } from 'next/router'
import HubStates from 'app/SeFriendly/HubStates'
import Footer from 'common/Footer'
import SeFriendlyBreadcrumbs from 'app/SeFriendly/SeFriendlyBreadcrumbs'
import SeFriendlyHeaderFilter from 'app/SeFriendly/SeFriendlyProviderSearch/SeFriendlyHeaderFilter'
import {
  googleMapLibraries,
  NAVBAR_BIG_HEIGHT,
  NAVBAR_NORMAL_HEIGHT,
} from 'constants/common'
import withGoogleMapsScript from 'hoc/withGoogleMapsScript'
import CallNowBookOnlineStickyCTA from 'common/CallNowBookOnlineStickyCTA'
import useBreakpoint from 'hooks/useBreakPoint'
import { stateAbbreviations } from 'app/SeFriendly/validateUsaState'
import { seFriendlyCities } from 'app/SeFriendly/cities'
import HowItWorksCarousel from 'app/SeFriendly/HowItWorksCarousel'
import AwardPanel from 'app/SeFriendly/AwardPanel'
import InsuranceInfo from 'app/SeFriendly/InsuranceInfo'
import ReviewPanel from 'app/SeFriendly/ReviewPanel'
import FAQ from 'app/SeFriendly/FAQ'
import { getProvidersBySpecialty } from 'app/SeFriendly/providers'
import { getFilters } from 'dataSource/ProviderSearch'
import { groupFilters } from 'app/ProviderSearch/utils'
import { FAQsForHubPsychiatrist } from 'app/SeFriendly/HubStates/constants'
import HubPageHeroSection from 'app/SeFriendly/HubPageHeroSection'
import HubProviderCard from 'app/SeFriendly/HubProviderCard'
import StateBookBanner from 'app/SeFriendly/Providers/StateBookBanner'
import { addKeywordToStoredSearchQuery } from 'utils/b2bDetails'
import { getRequestOrigin } from 'utils/getRequestOrigin'
import { generateSeFriendlyHubPageLdJsonSchema } from 'app/SeFriendly/utils'

const PSYCHIATRISTS_HUB_META = {
  title: 'Find a Psychiatrist',
  description:
    'Psychiatrists are medical doctors specializing in mental health that diagnose and manage medications for their clients, as well as offering therapy. Learn more.',
  faqSectionName: 'FAQs about psychiatry',
  popularCitiesListName: 'Most Popular Cities',
  availableStatesListName: 'States With Psychiatric Availability',
  serviceType: 'Psychiatry',
} as const

type Props = {
  validStates: { state: string[] }
  groupedFilters?: GroupedProviderFilter
  origin: string
}

const PsychiatristsHub: React.FC<Props> = ({
  validStates,
  groupedFilters,
  origin,
}) => {
  const router = useRouter()
  const { query } = router
  const partnershipCode = query?.partnershipCode
  const pageContainerRef = useRef<HTMLDivElement>(null)
  const { isMobileScreen, isLargeSize } = useBreakpoint()

  const isMobileOrTabScreen = isMobileScreen || isLargeSize

  const ldJsonSchemaData = useMemo(
    () =>
      generateSeFriendlyHubPageLdJsonSchema({
        providerType: providerTypes.PSYCHIATRISTS,
        metaTitle: PSYCHIATRISTS_HUB_META.title,
        metaDescription: PSYCHIATRISTS_HUB_META.description,
        serviceName: SeFriendlyHubPageTitle.PSYCHIATRY,
        serviceDescription: PSYCHIATRISTS_HUB_META.description,
        serviceType: PSYCHIATRISTS_HUB_META.serviceType,
        popularCitiesListName: PSYCHIATRISTS_HUB_META.popularCitiesListName,
        availableStatesListName: PSYCHIATRISTS_HUB_META.availableStatesListName,
        faqSectionName: PSYCHIATRISTS_HUB_META.faqSectionName,
        faqs: FAQsForHubPsychiatrist,
        validStates: validStates as Record<string, string[]>,
        directoryListItemLinkKey: 'url',
        requestOrigin: origin,
        requestPathname: `/${providerTypes.PSYCHIATRISTS}`,
      }),
    [validStates, origin]
  )

  useEffect(() => {
    if (partnershipCode) {
      addKeywordToStoredSearchQuery(partnershipCode as string)
    }
  }, [partnershipCode])

  return (
    <PageContainer
      ref={pageContainerRef}
      pageContainerClassName="bg-white"
      navContainerHeight={
        isMobileScreen ? NAVBAR_BIG_HEIGHT : NAVBAR_NORMAL_HEIGHT
      }
      callUsBannerNumber={{
        number: seFriendlyPhoneNumber.phoneNumber,
        formattedNumber: seFriendlyPhoneNumber.formattedPhoneNumber,
      }}
      navContent={<SeFriendlyHeaderFilter groupedFilters={groupedFilters} />}
    >
      <HeadComponent
        title={PSYCHIATRISTS_HUB_META.title}
        content={PSYCHIATRISTS_HUB_META.description}
        canonicalLink={`${origin}${router.asPath}`}
        ldJsonSchema={ldJsonSchemaData}
      >
        <HubPageHeroSection hubPageType={providerTypes.PSYCHIATRISTS} />
        <div className={styles.hub_container}>
          {isMobileOrTabScreen ? (
            <>
              <SeFriendlyBreadcrumbs
                pageType={SeFriendlyPageType.HUB}
                type="Psychiatrists"
              />
              <HubProviderCard
                type={providerTypes.PSYCHIATRISTS}
                groupedFilters={groupedFilters}
              />
              <HubStates
                validStates={validStates}
                type={providerTypes.PSYCHIATRISTS}
              />
            </>
          ) : (
            <>
              <div className={styles.hub_state_container}>
                <SeFriendlyBreadcrumbs
                  pageType={SeFriendlyPageType.HUB}
                  type="Psychiatrists"
                />
                <HubStates
                  validStates={validStates}
                  type={providerTypes.PSYCHIATRISTS}
                />
              </div>
              <HubProviderCard
                type={providerTypes.PSYCHIATRISTS}
                groupedFilters={groupedFilters}
              />
            </>
          )}
        </div>
        <HowItWorksCarousel HowItWorksCarouselData={HowItWorksPsychData} />
        <AwardPanel type="psychiatrists" />
        <ReviewPanel reviewsData={reviewPanelItems} />
        <InsuranceInfo />
        <div className={styles.hubPageFaqWrapper}>
          <FAQ
            title={PSYCHIATRISTS_HUB_META.faqSectionName}
            FAQsData={FAQsForHubPsychiatrist}
          />
        </div>
        <StateBookBanner
          sefriendlyPageType={SeFriendlyPageType.HUB}
          sefriendlyProvidersType={providerTypes.PSYCHIATRISTS}
        />
        <Footer hasExtendedBottom />
      </HeadComponent>
      <CallNowBookOnlineStickyCTA pageContainerRef={pageContainerRef} />
    </PageContainer>
  )
}

export const getServerSideProps: GetServerSideProps = async (context) => {
  const origin = getRequestOrigin(context.req)

  const states = Object.keys(stateAbbreviations)

  const validStates = {}
  const citiesGroupedByState = {}

  seFriendlyCities.forEach((city) => {
    if (citiesGroupedByState[city.state]) {
      citiesGroupedByState[city.state].push(city.city)
    } else {
      citiesGroupedByState[city.state] = [city.city]
    }
  })

  const getValidStates = async () => {
    await Promise.all(
      states.map(async (state) => {
        const providers = await getProvidersBySpecialty({
          specialty: specialties.PSYCHIATRIST,
          state,
        })

        if (providers.length > 4 && citiesGroupedByState[state]) {
          validStates[state] = citiesGroupedByState[state]
        }
      })
    )
  }

  await getValidStates()

  const response = await getFilters()
  const groupedFilters = groupFilters(response.result)

  return {
    props: { validStates, groupedFilters, origin },
  }
}

export default withGoogleMapsScript(PsychiatristsHub, [
  googleMapLibraries.GEOMETRY,
  googleMapLibraries.DRAWING,
  googleMapLibraries.PLACES,
])
